import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { createLogger } from '@/utils/logger';

const log = createLogger('ApiErrorHandler');

export interface ApiError {
  status?: number;
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * Phone validation error codes returned by the backend
 */
export const PHONE_ERROR_CODES = {
  PHONE_LOOKUP_FAILED: 'PHONE_LOOKUP_FAILED',
  PHONE_NOT_MOBILE: 'PHONE_NOT_MOBILE',
  PHONE_VALIDATION_ERROR: 'PHONE_VALIDATION_ERROR',
} as const;

export type PhoneErrorCode = (typeof PHONE_ERROR_CODES)[keyof typeof PHONE_ERROR_CODES];

/**
 * User-related error codes returned by the backend
 */
export const USER_ERROR_CODES = {
  DUPLICATE_USER_EXISTS: 'DUPLICATE_USER_EXISTS',
  USER_NOT_EDITABLE: 'USER_NOT_EDITABLE',
} as const;

export type UserErrorCode = (typeof USER_ERROR_CODES)[keyof typeof USER_ERROR_CODES];

/**
 * Signature request error codes returned by the POST request-document-signatures endpoint
 */
export const SIGNATURE_REQUEST_ERROR_CODES = {
  NOT_FOUND: 'NOT_FOUND',
  NO_BUNDLE: 'NO_BUNDLE',
  NO_SIGNATURE_DOCUMENTS: 'NO_SIGNATURE_DOCUMENTS',
  SIGNATURE_IN_PROGRESS: 'SIGNATURE_IN_PROGRESS',
  NO_ELIGIBLE_SIGNERS: 'NO_ELIGIBLE_SIGNERS',
  NO_MATCHING_SIGNERS: 'NO_MATCHING_SIGNERS',
  NO_SIGNATURE_FIELDS: 'NO_SIGNATURE_FIELDS',
  DOCUSEAL_ERROR: 'DOCUSEAL_ERROR',
  SPOUSE_NOT_CONFIGURED: 'SPOUSE_NOT_CONFIGURED',
  SPOUSE_SAME_AS_PRIMARY: 'SPOUSE_SAME_AS_PRIMARY',
} as const;

export type SignatureRequestErrorCode =
  (typeof SIGNATURE_REQUEST_ERROR_CODES)[keyof typeof SIGNATURE_REQUEST_ERROR_CODES];

/**
 * Checks if an error code is a phone validation error
 */
export const isPhoneValidationError = (code: string | undefined): code is PhoneErrorCode => {
  return code !== undefined && Object.values(PHONE_ERROR_CODES).includes(code as PhoneErrorCode);
};

/**
 * Handles and transforms API errors into a consistent format
 */
export const handleApiError = (error: unknown): ApiError => {
  log.debug('handleApiError called with:', error);

  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const responseData = error.response?.data;

    log.debug('AxiosError details:', {
      status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      responseData,
      message: error.message,
    });

    switch (status) {
      case 401:
        // Handle unauthorized - token expired or invalid
        return {
          status: 401,
          message: 'Your session has expired. Please log in again.',
          code: 'UNAUTHORIZED',
          details: responseData,
        };

      case 403:
        // Handle forbidden access
        return {
          status: 403,
          message: 'You do not have permission to access this resource.',
          code: 'FORBIDDEN',
          details: responseData,
        };

      case 404:
        // Handle not found
        return {
          status: 404,
          message: 'The requested resource was not found.',
          code: 'NOT_FOUND',
          details: responseData,
        };

      case 422:
        // Handle validation errors
        return {
          status: 422,
          message: responseData?.message || 'Validation error occurred.',
          code: 'VALIDATION_ERROR',
          details: responseData,
        };

      case 429:
        // Handle rate limiting
        return {
          status: 429,
          message: 'Too many requests. Please try again later.',
          code: 'RATE_LIMITED',
          details: responseData,
        };

      case 500:
        // Handle server errors - capture in Sentry
        Sentry.captureException(error, {
          tags: { errorType: 'server_error', statusCode: 500 },
          extra: { url: error.config?.url, method: error.config?.method },
        });
        return {
          status: 500,
          message: 'Internal server error. Please try again later.',
          code: 'SERVER_ERROR',
          details: responseData,
        };

      case 502:
      case 503:
      case 504:
        // Handle service unavailable errors - capture in Sentry
        Sentry.captureException(error, {
          tags: { errorType: 'service_unavailable', statusCode: status },
          extra: { url: error.config?.url, method: error.config?.method },
        });
        return {
          status: status,
          message: 'Service temporarily unavailable. Please try again later.',
          code: 'SERVICE_UNAVAILABLE',
          details: responseData,
        };

      default:
        // Handle other HTTP errors
        return {
          status: status,
          message: responseData?.message || error.message || 'An unexpected error occurred.',
          code: 'HTTP_ERROR',
          details: responseData,
        };
    }
  }

  // Handle network errors
  if (error instanceof Error) {
    if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
      // Capture network errors in Sentry
      Sentry.captureException(error, {
        tags: { errorType: 'network_error' },
      });
      return {
        message: 'Network connection error. Please check your internet connection.',
        code: 'NETWORK_ERROR',
      };
    }

    if (error.message.includes('timeout')) {
      // Capture timeout errors in Sentry
      Sentry.captureException(error, {
        tags: { errorType: 'timeout_error' },
      });
      return {
        message: 'Request timed out. Please try again.',
        code: 'TIMEOUT_ERROR',
      };
    }

    // Capture unknown errors in Sentry
    Sentry.captureException(error, {
      tags: { errorType: 'unknown_error' },
    });
    return {
      message: error.message || 'An unexpected error occurred.',
      code: 'UNKNOWN_ERROR',
    };
  }

  // Handle unknown errors - capture in Sentry
  Sentry.captureException(new Error('Unknown error type'), {
    tags: { errorType: 'unknown_error' },
    extra: { originalError: error },
  });
  return {
    message: 'An unexpected error occurred.',
    code: 'UNKNOWN_ERROR',
    details: error as Record<string, unknown>,
  };
};

/**
 * Determines if an error is retryable based on its type and status
 */
export const isRetryableError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;

    // Don't retry client errors (4xx)
    if (status && status >= 400 && status < 500) {
      return false;
    }

    // Retry server errors (5xx) and network errors
    return !status || status >= 500;
  }

  // Retry network and timeout errors
  if (error instanceof Error) {
    return (
      error.message.includes('Network Error') ||
      error.message.includes('timeout') ||
      error.message.includes('ERR_NETWORK')
    );
  }

  return false;
};

/**
 * Gets a user-friendly error message for display in the UI
 */
export const getUserFriendlyErrorMessage = (error: unknown): string => {
  const apiError = handleApiError(error);
  return apiError.message;
};

/**
 * Checks if an error is an authentication error that requires re-login
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.status === 401;
  }
  return false;
};

/**
 * Checks if an error is a permission error
 */
export const isPermissionError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.status === 403;
  }
  return false;
};

/**
 * Extracts validation error messages from a 400-level API error response
 * Returns a formatted string with all validation errors or null if not a validation error
 */
export const getValidationErrorMessage = (error: unknown): string | null => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const responseData = error.response?.data;

    // Check if this is a 400-level error with validation details
    if (status && status >= 400 && status < 500 && responseData?.errors) {
      const errors = responseData.errors as Record<string, string[]>;
      const errorMessages: string[] = [];

      // Extract all error messages from the errors object
      for (const [_field, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length > 0) {
          errorMessages.push(...messages);
        }
      }

      if (errorMessages.length > 0) {
        return errorMessages.join('. ');
      }
    }

    // If there's a title or message in the response, use that
    // Check both nested error structure (error.message) and flat structure (message)
    if (status && status >= 400 && status < 500) {
      return responseData?.error?.message || responseData?.title || responseData?.message || null;
    }
  }

  return null;
};

/**
 * Extracts phone validation error details from an API error response
 * Returns the error code and message if it's a phone validation error, null otherwise
 * @param error - The error object to extract validation details from
 * @param context - The context where the error occurred ('client' or 'preparer')
 */
export const getPhoneValidationError = (
  error: unknown,
  context: 'client' | 'preparer' = 'client'
): { code: PhoneErrorCode; message: string } | null => {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;

    // Check for error code in the response
    const errorCode = responseData?.error?.code || responseData?.code;

    if (isPhoneValidationError(errorCode)) {
      // Use the message from the API response, which includes specific details
      // like the phone type detected (e.g., "appears to be a landline")
      const message =
        responseData?.error?.message || responseData?.message || getDefaultPhoneErrorMessage(errorCode, context);

      return { code: errorCode, message };
    }
  }

  return null;
};

/**
 * Returns a default user-friendly message for phone validation error codes
 * @param code - The phone error code
 * @param context - The context where the error occurred ('client' or 'preparer')
 */
export const getDefaultPhoneErrorMessage = (
  code: PhoneErrorCode,
  context: 'client' | 'preparer' = 'client'
): string => {
  switch (code) {
    case PHONE_ERROR_CODES.PHONE_LOOKUP_FAILED:
      return 'Unable to validate phone number. Please verify the number is correct and try again.';
    case PHONE_ERROR_CODES.PHONE_NOT_MOBILE:
      if (context === 'preparer') {
        return 'Only mobile phone numbers are accepted. The number provided appears to be a landline or VoIP number. Please contact the client to get their actual mobile number.';
      }
      return 'Only mobile phone numbers are accepted. The number provided appears to be a landline or VoIP number. Please contact your preparer if you need to use a different phone number.';
    case PHONE_ERROR_CODES.PHONE_VALIDATION_ERROR:
      return 'An error occurred while validating the phone number. Please try again or contact support.';
    default:
      return 'Phone number validation failed. Please check the number and try again.';
  }
};

/**
 * Checks if an error code is a user-related error
 */
export const isUserError = (code: string | undefined): code is UserErrorCode => {
  return code !== undefined && Object.values(USER_ERROR_CODES).includes(code as UserErrorCode);
};

/**
 * Extracts user-related error details from an API error response
 * Returns the error code and message if it's a user error, null otherwise
 */
export const getUserError = (error: unknown): { code: UserErrorCode; message: string } | null => {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;

    // Check for error code in the response (supports both nested and flat structures)
    const errorCode = responseData?.error?.code || responseData?.code;

    if (isUserError(errorCode)) {
      // Use the message from the API response or a default message
      const message = responseData?.error?.message || responseData?.message || getDefaultUserErrorMessage(errorCode);

      return { code: errorCode, message };
    }
  }

  return null;
};

/**
 * Returns a user-friendly message for user-related error codes
 */
export const getDefaultUserErrorMessage = (code: UserErrorCode): string => {
  switch (code) {
    case USER_ERROR_CODES.DUPLICATE_USER_EXISTS:
      return 'A user with this email and phone number already exists. Please use the merge feature to combine these accounts.';
    case USER_ERROR_CODES.USER_NOT_EDITABLE:
      return 'This user cannot be edited through this interface.';
    default:
      return 'An error occurred while updating the user.';
  }
};

/**
 * Checks if an error code is a signature request error
 */
export const isSignatureRequestError = (code: string | undefined): code is SignatureRequestErrorCode => {
  return code !== undefined && Object.values(SIGNATURE_REQUEST_ERROR_CODES).includes(code as SignatureRequestErrorCode);
};

/**
 * Extracts signature request error details from an API error response.
 * Returns the error code and message if it's a signature request error, null otherwise.
 */
export const getSignatureRequestError = (
  error: unknown
): { code: SignatureRequestErrorCode; message: string } | null => {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;
    const errorCode = responseData?.error?.code || responseData?.code;

    if (isSignatureRequestError(errorCode)) {
      const message =
        responseData?.error?.message || responseData?.message || getDefaultSignatureRequestErrorMessage(errorCode);
      return { code: errorCode, message };
    }
  }
  return null;
};

/**
 * Returns a user-friendly message for signature request error codes
 */
export const getDefaultSignatureRequestErrorMessage = (code: SignatureRequestErrorCode): string => {
  switch (code) {
    case SIGNATURE_REQUEST_ERROR_CODES.NOT_FOUND:
      return 'The binder could not be found.';
    case SIGNATURE_REQUEST_ERROR_CODES.NO_BUNDLE:
      return 'No tax return bundle has been uploaded for this binder. Please upload the tax return bundle first.';
    case SIGNATURE_REQUEST_ERROR_CODES.NO_SIGNATURE_DOCUMENTS:
      return 'No documents requiring signatures were found in this bundle.';
    case SIGNATURE_REQUEST_ERROR_CODES.SIGNATURE_IN_PROGRESS:
      return 'A signature request is already in progress for this binder. Please wait for it to complete or void it before sending a new request.';
    case SIGNATURE_REQUEST_ERROR_CODES.NO_ELIGIBLE_SIGNERS:
      return 'No eligible signers were found. Please ensure at least one signer is configured and has a user account assigned.';
    case SIGNATURE_REQUEST_ERROR_CODES.NO_MATCHING_SIGNERS:
      return 'No signers matched the signature requirements. Please review the signer assignments for this return.';
    case SIGNATURE_REQUEST_ERROR_CODES.NO_SIGNATURE_FIELDS:
      return 'No signature fields were found in the documents. The signature template may need to be reconfigured.';
    case SIGNATURE_REQUEST_ERROR_CODES.DOCUSEAL_ERROR:
      return 'An error occurred with the e-signature service. Please try again or contact support if the issue persists.';
    case SIGNATURE_REQUEST_ERROR_CODES.SPOUSE_NOT_CONFIGURED:
      return 'This is a joint return but the spouse signer has not been assigned a user account. Please assign a user to the spouse signature requirement before requesting signatures.';
    case SIGNATURE_REQUEST_ERROR_CODES.SPOUSE_SAME_AS_PRIMARY:
      return 'The spouse signer is assigned to the same user account as the primary taxpayer. Each signer must have a separate user account for e-signature.';
    default:
      return 'Failed to request signatures. Please try again.';
  }
};
